/**
 * @fileoverview "Assign practice" from the instructor's student page (iOS
 * review B5). It used to be a popover on each practice item of the lesson
 * page; an assignment is the student's week, so it lives with the student.
 *
 * Step 1 picks an item from the student's most recent personal lessons
 * (`assignableLessons`); step 2 sets the optional targets and posts
 * `POST /lessons/{lesson_id}/assignments`. Class lessons are not offered:
 * assigning a class lesson's item fans out to the whole class.
 */

import { useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { isItemAssigned } from "@/lib/assignments";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Toggle } from "@/components/settings/SettingsSection";
import { FIELD_CLS } from "@/components/studio/EditableList";
import { Button } from "@/components/studio/primitives";

const EMPTY_FORM = { sessions: "", minutes: "", rehearsal: false };

const lessonDay = (lesson) => {
  const d = lesson?.created_at ? new Date(lesson.created_at) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
};

const LABEL_CLS = "font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {{lesson: object, items: {index: number, text: string}[]}[]} props.groups From `assignableLessons`.
 * @param {object[]} props.assignments The student's assignments, to mark items already assigned.
 * @param {boolean} [props.rehearsalsOn=true] Offer "Ask for a rehearsal recording".
 * @param {() => Promise<void>|void} [props.onAssigned] Refresh the caller's list.
 */
export function AssignPracticeDialog({ open, onOpenChange, groups, assignments, rehearsalsOn = true, onAssigned }) {
  const [picked, setPicked] = useState(null); // { lesson, index, text }
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setPicked(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const assign = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { practice_item_index: picked.index, rehearsal_requested: rehearsalsOn && form.rehearsal };
      if (form.sessions) body.target_sessions = Number(form.sessions);
      if (form.minutes) body.target_minutes = Number(form.minutes);
      await api.post(`/lessons/${picked.lesson.lesson_id}/assignments`, body);
      toast.success("Assigned");
      await onAssigned?.();
      handleOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't assign that"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="mx-4 max-h-[calc(100dvh-2rem)] max-w-[calc(100%-2rem)] overflow-y-auto border border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] md:max-w-md"
        data-testid="assign-practice-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Assign practice</DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">
            {picked ? "Set a target for this week, or leave it open." : "Pick something from a recent lesson."}
          </DialogDescription>
        </DialogHeader>

        {!picked ? (
          <div className="flex flex-col gap-5" data-testid="assign-practice-lessons">
            {groups.map(({ lesson, items }) => (
              <section key={lesson.lesson_id} data-testid={`assign-lesson-${lesson.lesson_id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate font-sans text-[13px] font-semibold text-[var(--text-secondary)]">{lesson.title}</span>
                  <span className="shrink-0 font-mono text-[12px] text-[var(--text-tertiary)]">{lessonDay(lesson)}</span>
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  {items.map((item) => {
                    const assigned = isItemAssigned(assignments, lesson.lesson_id, item.text);
                    return (
                      <button
                        key={item.index}
                        type="button"
                        disabled={assigned}
                        onClick={() => setPicked({ lesson, index: item.index, text: item.text })}
                        data-testid={`assign-pick-${lesson.lesson_id}-${item.index}`}
                        className="flex w-full items-start justify-between gap-3 rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-3.5 py-3 text-left font-sans text-[15px] leading-[1.4] text-[var(--text-primary)] transition-colors hover:bg-[var(--studio-interactive)] disabled:cursor-default disabled:text-[var(--text-tertiary)] disabled:hover:bg-[var(--studio-raised)]"
                      >
                        <span className="min-w-0 flex-1">{item.text}</span>
                        {assigned && <span className="shrink-0 font-sans text-[12px] text-[var(--text-tertiary)]">Assigned</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-3.5 py-3" data-testid="assign-picked">
              <p className="font-sans text-[15px] leading-[1.4] text-[var(--text-primary)]">{picked.text}</p>
              <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                {[picked.lesson.title, lessonDay(picked.lesson)].filter(Boolean).join(" · ")}
              </p>
            </div>

            <div>
              <p className={LABEL_CLS}>Track this week (optional)</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="font-sans text-xs text-[var(--text-tertiary)]">Sessions</span>
                  <input
                    type="number" min="1" max="50" placeholder="e.g. 3"
                    value={form.sessions}
                    onChange={(e) => setForm((f) => ({ ...f, sessions: e.target.value }))}
                    className={`${FIELD_CLS} mt-1 text-sm`}
                    data-testid="assign-sessions-input"
                  />
                </label>
                <label className="block">
                  <span className="font-sans text-xs text-[var(--text-tertiary)]">Minutes total</span>
                  <input
                    type="number" min="5" max="2000" placeholder="e.g. 60"
                    value={form.minutes}
                    onChange={(e) => setForm((f) => ({ ...f, minutes: e.target.value }))}
                    className={`${FIELD_CLS} mt-1 text-sm`}
                    data-testid="assign-minutes-input"
                  />
                </label>
              </div>
            </div>

            {rehearsalsOn && (
              <div className="flex items-center justify-between gap-4">
                <span className="font-sans text-sm text-[var(--text-secondary)]">Ask for a rehearsal recording</span>
                <Toggle
                  on={form.rehearsal}
                  onChange={(on) => setForm((f) => ({ ...f, rehearsal: on }))}
                  label="Ask for a rehearsal recording"
                  testId="assign-rehearsal-toggle"
                />
              </div>
            )}

            {error && (
              <p role="alert" className="font-sans text-[13px] text-[var(--state-danger)]" data-testid="assign-error">
                {error}
              </p>
            )}
          </div>
        )}

        {picked && (
          <DialogFooter className="gap-2">
            <Button variant="bare" onPress={reset} disabled={busy}>
              Back
            </Button>
            <Button onPress={assign} disabled={busy} data-testid="assign-confirm">
              {busy ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
